import type * as ents from '@attain-apps/entities'
import { Word } from '@attain-apps/string-builder'
import { TypeAsserter, TypeChecker } from '@attain-apps/type-guards'
import type * as ngGridstack from 'gridstack/dist/angular'

export type Frame = ngGridstack.NgGridStackWidget
export namespace Frame {
  export type Id = Word

  export interface Layout {
    x: number
    y: number
    w?: number
    h?: number
  }

  // Height and width may be undefined, as they are only set when greater than
  // 1. Since the library defines these values only in that case, we need to set
  // a default of 1.
  export const DEFAULT_SPAN = 1

  /** Returns a default {@link Frame} */
  export function generate(withDefault?: Partial<Frame>): Frame {
    return {
      h: 2,
      id: Word.generate(),
      selector: 'atn-mosaic-dev-widget-frame',
      w: 12,
      ...withDefault,
    }
  }

  /**
   * Groups widget frames by a given axis value (`x` or `y`) and calculates
   * their total span.
   *
   * Each group accumulates the total horizontal and vertical span of widgets at
   * the same position.
   *
   * @example
   * const widgets = [
   *   { id: 'a', x: 0, w: 2, h: 1 },
   *   { id: 'b', x: 1, w: 1, h: 1 },
   *   { id: 'c', x: 0, w: 1, h: 2 }
   * ];
   *
   * _groupByPositionOnAxis(widgets, 'x')
   * // => {
   * //   0: {
   * //     widgetFrames: [{ id: 'a', ... }, { id: 'c', ... }],
   * //     totalHorizontalSpan: 3, // 2 (a) + 1 (c)
   * //     totalVerticalSpan: 3    // 1 (a) + 2 (c)
   * //   },
   * //   1: {
   * //     widgetFrames: [{ id: 'b', ... }],
   * //     totalHorizontalSpan: 1,
   * //     totalVerticalSpan: 1
   * //   }
   * // }
   */
  function _groupByPositionOnAxis(
    widgetFrames: readonly Frame[],
    axis: 'x' | 'y',
  ): Frame.LookupByAxisPosition {
    return widgetFrames.reduce<Frame.LookupByAxisPosition>(
      (lookup, widgetFrame) => {
        const axisPosition = widgetFrame[axis]
        if (TypeChecker.isNumber(axisPosition)) {
          const group = (lookup[axisPosition] ??= {
            totalHorizontalSpan: 0,
            totalVerticalSpan: 0,
            widgetFrames: [],
          })

          group.widgetFrames.push(widgetFrame)
          group.totalVerticalSpan += widgetFrame.h ?? Frame.DEFAULT_SPAN
          group.totalHorizontalSpan += widgetFrame.w ?? Frame.DEFAULT_SPAN
        }
        return lookup
      },
      {},
    )
  }

  /**
   * Converts a flat list of {@link Frame} into a
   * {@link ents.core.mosaic.Page.Column.Element}.
   */
  function _toColumnElement(
    widgetFrames: readonly Frame[],
    gridStackCellHeight: number,
    widgetFrameLookup: Frame.Lookup,
  ): ents.core.mosaic.Page.Column.Element {
    /**
     * Case 1: Single {@link Frame}
     * - If only one widget frame is provided, create a
     *   {@link ents.core.mosaic.Page.Column.Element} containing a single
     *   {@link ents.core.mosaic.Widget.Ref}.
     */
    if (widgetFrames.length === 1 && widgetFrames[0].id) {
      const widgetRef = widgetFrameLookup[widgetFrames[0].id]
      // Ideally, we should never end up with `undefined` here—each
      // widgetFrameId is expected to have a related widgetRef at this point.
      // But in case something goes wrong, it's better to throw early and fail
      // fast than to proceed with invalid data.
      TypeAsserter.assertIsNotNullish(widgetRef)
      return {
        content: {
          widget: widgetRef,
        },
        height: Math.floor(
          (widgetFrames[0].h ?? Frame.DEFAULT_SPAN) * gridStackCellHeight,
        ),
      }
    }

    /**
     * Case 2: Multiple {@link Frame}
     * - When multiple widget frames are present, create a
     *   {@link ents.core.mosaic.Page.Column.Element} containing a
     *   {@link ents.core.mosaic.Page.Row}.
     *
     * @example
     *
     *  ----------------------------------------------------------------------
     * |                 |                 |                                 |
     * |                 |                 |          WidgetFrame 3          |
     * |                 |                 |                                 |
     * |                 |                 |                                 |
     * |                 |                 |                                 |
     * |  WidgetFrame 1  |  WidgetFrame 2  |---------------------------------|
     * |                 |                 |               |                 |
     * |                 |                 | WidgetFrame 4 |  WidgetFrame 5  |
     * |                 |                 |               |                 |
     * |                 |                 |               |                 |
     * |                 |                 |               |                 |
     *  ----------------------------------------------------------------------
     *
     *Reconstructed widgetFrames input:
     *[
     *  { id: 'widget-frame-1', x: 0, y: 0, w: 1, h: 2 },
     *  { id: 'widget-frame-2', x: 1, y: 0, w: 1, h: 2 },
     *  { id: 'widget-frame-3', x: 2, y: 0, w: 2, h: 1 },
     *  { id: 'widget-frame-4', x: 2, y: 1, w: 1, h: 1 },
     *  { id: 'widget-frame-5', x: 3, y: 1, w: 1, h: 1 },
     *]
     */

    /**
     * Step 1: Group the widgets by their X-axis position (horizontal alignment)
     * In this step we also calculate `totalHorizontalSpan` and
     * `totalVerticalSpan` for each group
     *
     * Explanation using the diagram: Widgets are first grouped based on the `x`
     * coordinate:
     *  - x = 0: [widget-frame-1]
     *  - x = 1: [widget-frame-2]
     *  - x = 2: [widget-frame-3, widget-frame-4]
     *  - x = 3: [widget-frame-5]
     */
    const lookupByXposition = _groupByPositionOnAxis(widgetFrames, 'x')

    /**
     * Step 2: Calculate the total vertical span (height) required for the row
     * container. The group with the largest `h` determines the overall height
     * of the container.
     *
     * Example:
     *  - x = 0: [widget-frame-1] → total height = 2
     *  - x = 1: [widget-frame-2] → total height = 2
     *  - x = 2: [widget-frame-3, widget-frame-4] → total height = 1 + 1 = 2
     *  - x = 3: [widget-frame-5] → total height = 1
     *
     * Therefore, the maximum vertical span across all groups is 2.
     */

    const containerVerticalSpan = Math.max(
      ...Object.values(lookupByXposition).map(group => group.totalVerticalSpan),
    )

    /**
     * Step 3: Extract stacks from the grouped widgets.
     *
     * This step identifies column-like stacks within the row container.
     * Starting from the X-axis groups, we iterate through each group and
     * determine whether to start a new column or continue the current one.
     *
     * For each group:
     *  - If the total height equals the container’s full height → start a new
     *    vertical stack.
     *  - If the total height is less than the container’s full height → append
     *    the group to the current vertical stack. This process creates
     *    horizontal slices of widgets that align across rows, forming logical
     *    substructures based on vertical span.
     *
     * Example:
     *  - x = 0: [widget-frame-1] → total height = 2 → start new column
     *  - x = 1: [widget-frame-2] → total height = 2 → start new column
     *  - x = 2: [widget-frame-3, widget-frame-4] → total height = 1 + 1 = 2 →
     *    start new column
     *  - x = 3: [widget-frame-5] → total height = 1 → add to previous column
     *
     * Resulting stacks: [[widget-frame-1], [widget-frame-2], [widget-frame-3,
     * widget-frame-4, widget-frame-5]]
     */
    const stacks = LookupByAxisPosition.groupIntoStacks(
      lookupByXposition,
      containerVerticalSpan,
      'vertical',
    )

    /**
     * Step 4: Convert each stack into a
     * {@link ents.core.mosaic.Page.Row.Element}.
     *
     * At this stage, each stack (from Step 3) is transformed into a row
     * element. A stack can either be:
     *  - a single widget → converted into a widget element
     *  - a group of widgets → converted into a nested column containing those
     *    widgets
     *
     * Example: Subcontainers: [[widget-1], [widget-2], [widget-3, widget-4,
     * widget-5]] Resulting row elements: [Widget, Widget, Column]
     *
     * /**
     * __NOTE__: This same approach is used to identify row-aligned stacks
     * inside a column container, by grouping along the Y-axis and evaluating
     * vertical spans.
     */
    return {
      content: {
        row: {
          elements: stacks.map(stack =>
            _toRowElement(stack, gridStackCellHeight, widgetFrameLookup),
          ),
        },
      },
      height: Math.floor(containerVerticalSpan * gridStackCellHeight),
    }
  }

  /**
   * Converts a flat list of {@link Frame} into a
   * {@link ents.core.mosaic.Page.Row.Element}.
   * /**
   *
   * @see {@link _toColumnElement} (contains a full example)
   */
  function _toRowElement(
    widgetFrames: readonly Frame[],
    gridStackCellHeight: number,
    widgetFrameLookup: Frame.Lookup,
  ): ents.core.mosaic.Page.Row.Element {
    // Case 1: Single Widget Frame
    if (widgetFrames.length === 1 && widgetFrames[0].id) {
      const widgetRef = widgetFrameLookup[widgetFrames[0].id]
      // Ideally, we should never end up with `undefined` here—each
      // widgetFrameId is expected to have a related widgetRef at this point.
      // But in case something goes wrong, it's better to throw early and fail
      // fast than to proceed with invalid data.
      TypeAsserter.assertIsNotNullish(widgetRef)
      return {
        content: {
          widget: widgetRef,
        },
        span: widgetFrames[0].w ?? Frame.DEFAULT_SPAN,
      }
    }

    /**
     * Case 2: Multiple Widget Frames -When multiple widget frames are present,
     * create a {@link ents.core.mosaic.Page.Row.Element} containing a
     * {@link ents.core.mosaic.Page.Column}. The elements of the column are
     * determined by following these steps:
     */

    // Step 1: Group the widgets by their Y-axis position (vertical alignment)
    const lookupByYposition = _groupByPositionOnAxis(widgetFrames, 'y')

    // Step 2: Calculate the total width span of the column container.
    const containerHorizontalSpan = Math.max(
      ...Object.values(lookupByYposition).map(
        group => group.totalHorizontalSpan,
      ),
    )

    // Step 3: Extract stacks from the grouped widgets.
    const stacks = LookupByAxisPosition.groupIntoStacks(
      lookupByYposition,
      containerHorizontalSpan,
      'horizontal',
    )

    return {
      content: {
        column: {
          // Step 4: Convert each stack into a column element. This can either
          // be a widget or a nested row.
          elements: stacks.map(stack =>
            _toColumnElement(stack, gridStackCellHeight, widgetFrameLookup),
          ),
        },
      },
      span: containerHorizontalSpan,
    }
  }

  /**
   * Converts a flat list of {@link Frame} into a root
   * {@link ents.core.mosaic.Page.Column} structure.
   *
   * @see {@link _toColumnElement} (contains a full example)
   */
  export function toRootElement(
    widgetFrames: readonly Frame[],
    gridStackCellHeight: number,
    widgetFrameLookup: Frame.Lookup,
  ): ents.core.mosaic.Page.Column {
    // Step 1: Group widgets by their vertical (Y-axis) position.
    const lookupByYposition = _groupByPositionOnAxis(widgetFrames, 'y')

    // Step 2: Calculate the total width span of the container (column).
    const containerHorizontalSpan = Math.max(
      ...Object.values(lookupByYposition).map(
        group => group.totalHorizontalSpan,
      ),
    )

    // Step 3: Extract stacks from the grouped widgets.
    const stacks = LookupByAxisPosition.groupIntoStacks(
      lookupByYposition,
      containerHorizontalSpan,
      'horizontal',
    )

    return {
      // Step 4: Convert each stack into a column element. This can either be a
      // widget or a nested row.
      elements: stacks.map(stack =>
        _toColumnElement(stack, gridStackCellHeight, widgetFrameLookup),
      ),
    }
  }

  /**
   * Recursively extracts a list of {@link Frame} from a list of
   * {@link ents.core.mosaic.Page.Row.Element}.
   *
   * This function iterates over each {@link ents.core.mosaic.Page.Row.Element}
   * in the provided row, calculating its width based on the grid cell size. It
   * generates a flat list of {@link Frame} objects. If an element
   * contains a {@link ents.core.mosaic.Widget.Ref}, it directly creates a
   * {@link Frame}. If the element contains a nested
   * {@link ents.core.mosaic.Page.Column}, it recursively processes the column
   * elements.
   *
   */
  function _fromRowElements(
    rowElements: readonly ents.core.mosaic.Page.Row.Element[],
    gridStackCellHeight: number,
    wfLayout: Frame.Layout,
    wfLookup: Frame.Lookup,
  ): Frame[] {
    let currentX = wfLayout.x

    return rowElements.flatMap(rowElement => {
      const widthSpan = rowElement.span

      if (rowElement.content.widget) {
        // Case 1: Direct widget inside the row
        const widgetFrame = Frame.generate({
          h: wfLayout.h,
          w: widthSpan,
          x: currentX,
          y: wfLayout.y,
        })
        if (widgetFrame.id && rowElement.content.widget) {
          wfLookup[widgetFrame.id] = rowElement.content.widget
        }
        // Shift x-position for the next widget in the row
        currentX += widthSpan
        return [widgetFrame]
      }

      if (rowElement.content.column) {
        // Case 2: Nested column inside the row
        const nestedWidgetFrames = fromColumnElements(
          rowElement.content.column.elements,
          gridStackCellHeight,
          { ...wfLayout, w: widthSpan, x: currentX },
          wfLookup,
        )
        // Shift x-position for the next widget in the row
        currentX += widthSpan
        return nestedWidgetFrames
      }

      // Skip unknown content
      return []
    })
  }

  /**
   * Recursively extracts a list of {@link Frame} from a list of
   * {@link ents.core.mosaic.Page.Column.Element}.
   *
   * This function iterates over each
   * {@link ents.core.mosaic.Page.Column.Element} in the given column,
   * calculating its height based on the grid cell size. It then generates a
   * flat list of {@link Frame} objects. If an element contains a
   * {@link ents.core.mosaic.Widget.Ref}, it directly creates a
   * {@link Frame}. If the element contains a nested
   * {@link ents.core.mosaic.Page.Row}, it will recursively extract frames from
   * the row's elements.
   *
   */
  export function fromColumnElements(
    columnElements: readonly ents.core.mosaic.Page.Column.Element[],
    gridStackCellHeight: number,
    wfLayout: Frame.Layout,
    wfLookup: Frame.Lookup,
  ): Frame[] {
    let currentY = wfLayout.y

    return columnElements.flatMap(columnElement => {
      const heightSpan = Math.round(columnElement.height / gridStackCellHeight)

      if (columnElement.content.widget) {
        // Case 1: Direct widget inside the column
        const widgetFrame = Frame.generate({
          h: heightSpan,
          w: wfLayout.w,
          x: wfLayout.x,
          y: currentY,
        })
        if (widgetFrame.id && columnElement.content.widget) {
          wfLookup[widgetFrame.id] = columnElement.content.widget
        }
        // Shift y-position for the next widget in the column
        currentY += heightSpan
        return [widgetFrame]
      }

      if (columnElement.content.row) {
        // Case 2: Nested row inside the column
        const nestedWidgetFrames = _fromRowElements(
          columnElement.content.row.elements,
          gridStackCellHeight,
          { ...wfLayout, h: heightSpan, y: currentY },
          wfLookup,
        )
        // Shift y-position for the next widget in the column
        currentY += heightSpan
        return nestedWidgetFrames
      }

      // Skip unknown content
      return []
    })
  }

  export type Lookup = Partial<Record<Id, ents.core.mosaic.Widget.Ref>>

  export interface LayoutGroup {
    widgetFrames: Frame[]
    totalHorizontalSpan: number
    totalVerticalSpan: number
  }

  /**
   * A lookup of {@link Frame.LayoutGroup} grouped by their position on
   * the X or Y axis.
   */
  export type LookupByAxisPosition = Record<number, Frame.LayoutGroup>

  namespace LookupByAxisPosition {
    /**
     * Groups widget frames into stacks (rows or columns) based on their axis
     * position and total span.
     *
     * This method is typically used for organizing widgets in a grid layout. It
     * assumes that widgets aligned along the same axis position (x or y) form a
     * row or column.
     *
     * If a group at a given axis position has a total span (height or width)
     * that equals the full container's span, it starts a new stack. Otherwise,
     * the group is considered incomplete and merged into the previous stack.
     * This allows partially-filled rows/columns to be grouped together
     * logically.
     *
     * @example
     * const positionLookup = {
     *   0: {
     *     widgetFrames: [{ id: 'a', h: 2 }],
     *     totalVerticalSpan: 2,
     *     totalHorizontalSpan: 1,
     *   },
     *   1: {
     *     widgetFrames: [{ id: 'b', h: 1 }],
     *     totalVerticalSpan: 1,
     *     totalHorizontalSpan: 1,
     *   },
     *   2: {
     *     widgetFrames: [{ id: 'c', h: 2 }],
     *     totalVerticalSpan: 2,
     *     totalHorizontalSpan: 1,
     *   },
     * };
     *
     * groupIntoStacks(positionLookup, 2, 'vertical');
     * // => [
     * //   [ { id: 'a', h: 2 }, { id: 'b', h: 1 } ], // row 0 and 1 merged (1 is partial)
     * //   [ { id: 'c', h: 2 } ]                    // row 2 is full, starts new group
     * // ]
     */
    export function groupIntoStacks(
      positionLookup: LookupByAxisPosition,
      fullStackSpan: number,
      // 'vertical' means stacking by Y axis, 'horizontal' means stacking by X
      // axis
      stackDirection: 'vertical' | 'horizontal',
    ): Frame[][] {
      const stacks: Frame[][] = []

      // eslint-disable-next-line no-undef-init
      let currentStack: Frame[] | undefined = undefined

      const sortedPositions = Object.keys(positionLookup)
        .map(Number)
        .sort((a, b) => a - b)

      for (const position of sortedPositions) {
        const { totalHorizontalSpan, totalVerticalSpan, widgetFrames } =
          positionLookup[position]

        const totalGroupSpan =
          stackDirection === 'vertical'
            ? totalVerticalSpan
            : totalHorizontalSpan

        if (totalGroupSpan === fullStackSpan) {
          // This group spans fully, so it starts a new stack
          currentStack = widgetFrames
          stacks.push(currentStack)
        } else {
          // This group is smaller; merge into the current stack
          if (!currentStack) {
            stacks.push((currentStack = []))
          }
          currentStack.push(...widgetFrames)
        }
      }

      return stacks
    }
  }
}

// TODO: Temporary solution to enable the use of the widgetRefs array in the effect
// for loading widget previews when the page loads in edit mode.
// This logic is expected to be removed once the proper features and/or plugin are implemented.
export namespace WidgetRef {
  /**
   * Constructs an array of {@link ents.core.mosaic.Widget.Ref} from the given
   * {@link ents.core.mosaic.Page.Content}.
   */
  export function fromContent(
    content: ents.core.mosaic.Page.Content,
  ): ents.core.mosaic.Widget.Ref[] {
    if (content.root?.elements) {
      return _collectFromColumnElements(content.root.elements)
    } else if (content.segments) {
      return content.segments.flatMap(segment =>
        _collectFromColumnElements(segment.root.elements),
      )
    }
    return []
  }

  /**
   * Recursively processes array of {@link ents.core.mosaic.Page.Row.Element}
   * and collects {@link ents.core.mosaic.Widget.Ref}.
   */
  function _collectFromRowElements(
    rowElements: readonly ents.core.mosaic.Page.Row.Element[],
  ): ents.core.mosaic.Widget.Ref[] {
    return rowElements.flatMap(rowElement => {
      const content = rowElement.content
      if (content.widget) {
        return [content.widget]
      } else if (content.column) {
        return _collectFromColumnElements(content.column.elements)
      }
      return []
    })
  }

  /**
   * Recursively processes array of {@link ents.core.mosaic.Page.Column.Element}
   * and collects {@link ents.core.mosaic.Widget.Ref}.
   */
  function _collectFromColumnElements(
    columnElements: readonly ents.core.mosaic.Page.Column.Element[],
  ): ents.core.mosaic.Widget.Ref[] {
    return columnElements.flatMap(columnElement => {
      const content = columnElement.content
      if (content.widget) {
        return [content.widget]
      } else if (content.row) {
        return _collectFromRowElements(content.row.elements)
      }
      return []
    })
  }
}
